import { useFrame, Canvas } from "@react-three/fiber";
import React, { useRef, useMemo } from "react";
import * as THREE from "three";
import { SphereGeometry, MeshBasicMaterial, Mesh } from "three";
import { LineBasicMaterial, BufferGeometry, Line, Vector3 } from "three";

// Utility function to generate random points
export const generateRandomPoints = (numPoints, range) => {
    const points = [];
    for (let i = 0; i < numPoints; i++) {
        const x = Math.random() * range - range / 2;
        const y = Math.random() * range - range / 2;
        const z = Math.random() * range - range / 2;
        points.push([x, y, z]);
    }
    return points;
};
export const CustomSphere = ({ position, color }) => {
    const ref = useRef();

    // Dynamically update the position of the sphere in each frame
    useFrame(() => {
        ref.current.position.set(position[0], position[1], position[2]);
    });

    const material = new MeshBasicMaterial({ color: color });
    const geometry = new SphereGeometry(0.1, 16, 16); // Adjust radius and segments

    return <mesh ref={ref} geometry={geometry} material={material} />;
};

// Custom Line component
export const CustomLine = ({ pointA, pointB, color }) => {
    const ref = useRef();

    useFrame(() => {
        if (ref.current) {
            const positions = ref.current.geometry.attributes.position.array;
            positions[0] = pointA[0];
            positions[1] = pointA[1];
            positions[2] = pointA[2];
            positions[3] = pointB[0];
            positions[4] = pointB[1];
            positions[5] = pointB[2];
            ref.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    return (
        <line ref={ref}>
            <bufferGeometry attach="geometry">
                <bufferAttribute attachObject={["attributes", "position"]} array={new Float32Array([...pointA, ...pointB])} count={2} itemSize={3} />
            </bufferGeometry>
            <lineBasicMaterial attach="material" color={color} />
        </line>
    );
};

// Main Constellation component with animation
export const Constellation = ({ numPoints = 20, range = 10, color }) => {
    // Generate points and lines only once using useMemo
    const initialPoints = useMemo(() => generateRandomPoints(numPoints, range), [numPoints, range]);

    const animatedPoints = useRef([...initialPoints]); // To store animated positions

    // Use frame to animate the points (sine wave-based animation)
    useFrame(({ clock }) => {
        const time = clock.getElapsedTime();
        animatedPoints.current.forEach((point, idx) => {
            const driftAmount = 0.005; // Adjust this for more or less drift
            if (idx % 2 === 0) {
                point[0] = initialPoints[idx][0] + Math.sin(time + idx) * driftAmount;
                point[1] = initialPoints[idx][1] + (Math.cos(time + idx) * driftAmount) / 2;
            }
            if (idx % 2 === 1) {
                point[0] = initialPoints[idx][0] + (Math.sin(time + idx) * driftAmount) / 2;
                point[1] = initialPoints[idx][1] + Math.cos(time + idx) * driftAmount;
            }
        });
    });

    // Generate line connections only once
    const lines = useMemo(() => {
        const connections = [];
        let numConnections = 0;

        for (let i = 0; i < numPoints; i++) {
            for (let j = i + 1; j < numPoints; j++) {
                if (Math.random() > 0.7 && numConnections < numPoints * 0.5) {
                    connections.push([i, j]); // Store indices instead of points
                    numConnections++;
                }
            }
        }
        return connections;
    }, [initialPoints, numPoints]);

    return (
        <group>
            {animatedPoints.current.map((point, idx) => (
                <CustomSphere key={idx} position={point} color={color} />
            ))}
            {lines.map((lineIndices, idx) => {
                const [start, end] = lineIndices;
                return <CustomLine key={idx} pointA={animatedPoints.current[start]} pointB={animatedPoints.current[end]} color={color} />;
            })}
        </group>
    );
};

export const StarrySky = ({ numStars = 1000, range = 500, color }) => {
    const starPositions = useMemo(() => generateRandomPoints(numStars, range), [numStars, range]);

    const animatedPoints = useRef([...starPositions]); // To store animated positions

    // Use frame to animate the points (sine wave-based animation)
    useFrame(({ clock }) => {
        const time = clock.getElapsedTime();
        animatedPoints.current.forEach((point, idx) => {
            const driftAmount = 0.005; // Adjust this for more or less drift
            if (idx % 2 === 0) {
                point[0] = starPositions[idx][0] + Math.sin(time + idx) * driftAmount;
                point[1] = starPositions[idx][1] + (Math.cos(time + idx) * driftAmount) / 2;
            }
            if (idx % 2 === 1) {
                point[0] = starPositions[idx][0] + (Math.sin(time + idx) * driftAmount) / 2;
                point[1] = starPositions[idx][1] + Math.cos(time + idx) * driftAmount;
            }
        });
    });

    return (
        <group>
            {animatedPoints.current.map((position, idx) => (
                <CustomSphere key={idx} position={position} color={color} />
            ))}
        </group>
    );
};

export const StarrySkyCanvas = ({ color }) => {
    return (
        <Canvas camera={{ position: [0, 0, 15], fov: 50 }} style={{ height: "100vh", width: "100vw" }}>
            <ambientLight />
            <StarrySky numStars={1000} range={500} color={color} /> {/* Starry sky background */}
        </Canvas>
    );
};

// Canvas component
export const ConstellationCanvas = ({ color, style, camera }) => {
    return (
        <Canvas camera={camera} style={style}>
            <ambientLight color={0xffffff} intensity={1.5} />
            <Constellation numPoints={8} range={10} color={color} />
        </Canvas>
    );
};
